import { motion, useSpring, useTransform, useInView, AnimatePresence, cubicBezier, useAnimate, stagger } from 'framer-motion';
import { useRef, useState, useEffect, useContext } from 'react';
import { useInViewScroll, MotionImage, useSanitizedTranslation, PromoBlock, XumoButton as Button, StackedContent, TextBlock } from 'ui';

export interface InfoCardGridProps {
  stackedContent: any;
  infoCards: InfoCardGridProps[];
}

export interface InfoCardProps {
  title: string;
  body: string;
}

const InfoCard = ({ title, body }: InfoCardProps) => {
  const { t } = useSanitizedTranslation();

  const [ref, animate] = useAnimate();
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });

  useEffect(() => {
    if (inView) {
      animate(
        'div, p, h1, h2, h3, h4, .img, .content, .btn',
        {
          y: [30, 0],
          opacity: [0, 1],
        },
        {
          ease: [0, 1, 1, 1],
          duration: 1.2,
          delay: stagger(0.33),
          deltaRest: 0.000001,
          deltaSpeed: 0.000001,
        },
      );
    }
  }, [inView]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
      className="col-span-1 mx-auto max-w-[364px] rounded-3xl bg-white p-6"
    >
      <h3 data-i18n={t(title)} dangerouslySetInnerHTML={{ __html: t(title) }} className="gradient-text mb-6 text-center md:mb-12 md:text-left 2xl:w-[90%]" />
      <p data-i18n={t(body)} dangerouslySetInnerHTML={{ __html: t(body) }} className="text-center text-xumoDarkPlum md:text-left" />
    </motion.div>
  );
};

const InfoCardGrid = ({ stackedContent, infoCards }: InfoCardGridProps) => {
  const { ref, progress: amt } = useInViewScroll([0, 1], ['start 100vh', 'end 0vh']);

  return (
    <motion.section className="relative z-10 flex w-full justify-center bg-white px-2 py-12 md:px-0 lg:py-32" data-testid={'text-section'}>
      <div className={`wrapper bg-dark-gradient w-full rounded-3xl py-16 md:py-24`}>
        <div className="wrapper">
          <StackedContent fullalways fullheight mobileCenterLg center space content={stackedContent} />
        </div>
        <div className="mt-12 grid w-full grid-cols-1 justify-center gap-8 md:mt-24 md:grid-cols-2 xl:grid-cols-4">
          {infoCards.map((card, index) => (
            <InfoCard key={'infoCard' + index} title={card.title} body={card.body} />
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default InfoCardGrid;
